/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';

import SessionJoinRow from 'bundles/course-sessions/components/SessionJoinRow';
import SessionSwitchRow from 'bundles/course-sessions/components/SessionSwitchRow';
import type { PropsFromWithSessionLabel } from 'bundles/course-sessions/utils/withSessionLabel';
import withSessionLabel from 'bundles/course-sessions/utils/withSessionLabel';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import type OnDemandLearnerSessions from 'bundles/naptimejs/resources/onDemandLearnerSessions.v1';

import _t from 'i18n!nls/ondemand';

import 'css!./__styles__/SessionSwitchInfo';

type PropsFromCaller = {
  upcomingEnrollableSessions: OnDemandLearnerSessions[];
  handleSessionSwitch: (session: OnDemandLearnerSessions) => void;
  showChangesDescription?: (session: OnDemandLearnerSessions) => void;
  isSelfServeSession: boolean;
  courseId: string;
  id: string;
};

type PropsToComponent = PropsFromCaller & PropsFromWithSessionLabel;

const style = {
  headerText: css`
    font-size: var(--cds-font-size-300);
  `,
};

const SessionSwitchInfo = (props: PropsToComponent) => {
  const {
    sessionLabel,
    upcomingEnrollableSessions,
    handleSessionSwitch,
    courseId,
    showChangesDescription,
    isSelfServeSession,
    id,
  } = props;

  const getTitleText = (multipleJoinableSessions: boolean) => {
    if (isSelfServeSession) {
      return sessionLabel === 'session' ? _t('Pick a session') : _t('Pick a schedule');
    } else if (multipleJoinableSessions) {
      return sessionLabel === 'session'
        ? _t('Choose a session that works for you')
        : _t('Choose a schedule that works for you');
    } else {
      return sessionLabel === 'session' ? _t('Switch sessions') : _t('Switch schedules');
    }
  };

  const getDescriptionText = (multipleJoinableSessions: boolean) => {
    if (multipleJoinableSessions) {
      return sessionLabel === 'session'
        ? _t(
            `We’ve saved your progress, so you’ll be able to pick up right where you left off. Join the
        recommended session and you’ll be right on track. `
          )
        : _t(
            `We’ve saved your progress, so you’ll be able to pick up right where you left off. Join the
          recommended schedule and you’ll be right on track. `
          );
    } else {
      return _t('We’ve saved your progress, so you’ll be able to pick up right where you left off. ');
    }
  };

  const showWeekOneWarning = upcomingEnrollableSessions[0] && !upcomingEnrollableSessions[0].hasStarted;
  const joinableSessions = upcomingEnrollableSessions.filter(
    (session) => session.enrollmentEnded || session.isEnrollableNow
  );
  const multipleSessions = joinableSessions.length > 1;
  const showRecommended = multipleSessions;

  const sessionRows = upcomingEnrollableSessions.map((session) => {
    return isSelfServeSession ? (
      <SessionJoinRow session={session} handleSessionSwitch={handleSessionSwitch} key={session.id} />
    ) : (
      <SessionSwitchRow
        session={session}
        courseId={courseId}
        handleSessionSwitch={handleSessionSwitch}
        showChangesDescription={showChangesDescription}
        key={session.id}
        isRecommended={showRecommended && session.sessionSwitch.isRecommendedSwitch}
      />
    );
  });

  return (
    <div className="rc-SessionSwitchInfo">
      <Typography2 component="h2" css={style.headerText} id={id}>
        {getTitleText(multipleSessions)}
      </Typography2>

      <div className="content">
        {!isSelfServeSession && (
          <p className="body-1-text">
            {getDescriptionText(multipleSessions)}
            <a href="https://learner.coursera.help/hc/articles/208279776" target="_blank" rel="noreferrer noopener">
              {_t('Learn more.')}
            </a>
          </p>
        )}

        {!isSelfServeSession && showWeekOneWarning && (
          <p className="body-1-text">
            {sessionLabel === 'session'
              ? _t(
                  `You will be able to access lectures, readings, and all Week 1 materials now. You’ll have access to
                all materials when your new session starts. `
                )
              : _t(
                  `You will be able to access lectures, readings, and all Week 1 materials now. You’ll have access to
                all materials when your new schedule starts. `
                )}
          </p>
        )}

        {isSelfServeSession && (
          <p className="body-1-text">
            {sessionLabel === 'session'
              ? _t(
                  `Select a session to start and you’ll have access to lectures, readings, and all Week 1 materials. 
                  All materials will be accessible when your new session starts.`
                )
              : _t(
                  `Select a schedule to start and you’ll have access to lectures, readings, 
                    and all Week 1 materials. All materials will be accessible when your new schedule starts.`
                )}
          </p>
        )}

        {upcomingEnrollableSessions.length > 0 ? (
          <ul className="sessions-list">{sessionRows}</ul>
        ) : (
          <p>
            {sessionLabel === 'session'
              ? _t('There are no upcoming sessions available at this time.')
              : _t('There are no upcoming schedules available at this time.')}
          </p>
        )}
      </div>
    </div>
  );
};

export default withSessionLabel<PropsFromCaller>(SessionSwitchInfo);
